import { useState, type ReactNode } from 'react';
import { clsx } from 'clsx';
import { useId } from 'react';

import styles from './ExpandableInfoPanel.module.css';

import { Icon } from 'components/Icon';

export interface ExpandableInfoPanelProps {
  /** Label for the trigger button that toggles the expandable panel. */
  triggerLabel: string;
  /** Main header displayed inline left of the trigger button. */
  mainHeader?: ReactNode | string;
  /** Aria-label for the button when the panel is collapsed. */
  collapseLabel?: string;
  /** Aria-label for the button when the panel is expanded. */
  expandLabel?: string;
  /** Content to be displayed inside the expandable panel. */
  content: ReactNode | string;
  /** Optional content area on in seperate box on right side of expanded content. */
  note?: ReactNode | string;
  /** Optional state, if true; sets the expandable panel as 'open' by default. */
  defaultOpen?: boolean;
}

export const ExpandableInfoPanel = ({
  triggerLabel,
  mainHeader,
  collapseLabel = '',
  expandLabel = '',
  content,
  note = '',
  defaultOpen = false,
}: ExpandableInfoPanelProps) => {
  const [active, setActive] = useState(defaultOpen);
  const contentId = useId();

  return (
    <div className={styles['expandable-info-panel_wrapper']}>
      <div className={styles['expandable-info-panel_header']}>
        {mainHeader}
        <button
          className={clsx(styles['expandable-info-panel_trigger'], {
            [styles['expandable-info-panel_trigger--active']]: active,
          })}
          type="button"
          aria-expanded={active}
          aria-controls={contentId}
          aria-label={`${triggerLabel} - ${active ? collapseLabel : expandLabel}`}
          onClick={() => setActive(!active)}
        >
          <Icon name={active ? 'SquareInfoSolid' : 'SquareInfo'} size={24} />
          {triggerLabel}
          <Icon name={active ? 'CheveronUp' : 'CheveronDown'} size={16} />
        </button>
      </div>
      {active && (
        <div id={contentId} className={styles['expandable-info-panel_content-wrapper']}>
          <Icon name="SquareInfo" size={24} />
          {!note ? (
            <div>{content}</div>
          ) : (
            <div className={styles['expandable-info-panel_content-with-note-container']}>
              <div>{content}</div>
              <div className={styles['expandable-info-panel_note']}>{note}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
